// ─── Public Status Page — Danish copy ────────────────────────────────
//
// ONE flat object, no i18n library. The public page is Danish-only: it replaces
// status.cb.dk, whose audience is Danish customers, so there is no language
// switcher to build and no runtime bundle to ship. A translation layer here
// would be pure cost for zero readers.
//
// NEVER import this from apps/web/src/pages or apps/web/src/components — the
// PCP ADMIN area (Settings → Status Page, the incident editor, the subscriber
// list) stays ENGLISH, exactly like every other admin surface. The shared
// package already carries both sides (STATUS_*_LABELS_DA / _EN); admin code
// takes the _EN maps.
//
// Interpolated strings are FUNCTIONS, not templates with placeholders — so the
// call site cannot forget an argument and no format parser is needed.
//
// Dates/times/numbers are NOT in here: they are formatted from the visitor's own
// regional settings by ./lib/format.ts (see CLAUDE.md — never a hardcoded
// locale). Copy here only supplies the surrounding words.

import { STATUS_FILTER_LABEL_LABELS_DA } from '@pcp/shared';

export const da = {
  // ── Brand / chrome ────────────────────────────────────────────────
  // C&B is the COMPANY, and this is the company's status page — it replaces
  // status.cb.dk. The products it covers appear on the page as COMPONENTS and
  // as audiences, and they are named there, by the operator, in data. None of
  // them owns the page, so no product name belongs in the chrome — and none is
  // hardcoded anywhere in this file. `brand` is the one place the company is
  // named: the header logomark's alt text, the footer link and the document
  // title all read it from here.
  brand: 'C&B',
  tagline: 'Real Estate · Real People',
  pageTitle: 'Driftsstatus',
  skipToContent: 'Gå til indhold',
  footerNote: 'Her kan du følge driften af C&B’s tjenester. Siden opdateres automatisk.',
  audienceAll: 'Alle tjenester',
  close: 'Luk',

  // ── Loading / failure ─────────────────────────────────────────────
  loading: 'Henter driftsstatus …',
  loadFailed: 'Driftsstatus kunne ikke hentes lige nu.',
  retry: 'Prøv igen',
  refresh: 'Opdatér',
  fatalTitle: 'Siden kunne ikke vises',
  fatalBody: 'Der opstod en uventet fejl under visningen af driftsstatus. Genindlæs siden for at prøve igen.',
  reload: 'Genindlæs siden',

  // ── Snapshot / degraded mode ──────────────────────────────────────
  degradedTitle: 'Viser senest gemte status',
  degradedBody:
    'Vi kan ikke kontakte driftssystemet lige nu, så tallene nedenfor er den senest gemte version. Selve tjenesterne kan derfor have ændret status, siden siden blev gemt.',
  degradedAt: (when: string) => `Sidst opdateret ${when}`,
  degradedOffline: 'Vi kan heller ikke hente den gemte version. Kontrollér din internetforbindelse.',
  lastUpdated: (when: string) => `Opdateret ${when}`,
  lastChecked: (when: string) => `Sidst kontrolleret ${when}`,

  // ── Overall banner ────────────────────────────────────────────────
  overallOperational: 'Alle systemer kører normalt',
  overallDegraded: 'Nedsat ydeevne på et eller flere systemer',
  overallMaintenance: 'Planlagt vedligeholdelse i gang',
  overallOutage: 'Driftsforstyrrelse på et eller flere systemer',
  overallUnknown: 'Status er ukendt lige nu',
  overallActiveIncidents: 'Aktive hændelser',

  // ── Overview (the 90-day bar) ─────────────────────────────────────
  overviewHeading: 'Overblik',
  overviewLegendOlder: 'Ældre',
  overviewLegendToday: 'I dag',
  overviewUptime: 'oppetid',
  overviewDays: (days: number) => `Seneste ${days} dage`,
  overviewNoData: 'Ingen data',

  // ── Components ────────────────────────────────────────────────────
  componentsHeading: 'Systemer',
  componentsEmpty: 'Der er ingen systemer at vise for dette område.',
  externalLink: 'Åbn tjenesten',
  expandComponent: (name: string) => `Vis undersystemer under ${name}`,
  collapseComponent: (name: string) => `Skjul undersystemer under ${name}`,
  ungroupedHeading: 'Øvrige',

  // ── Component detail (/component/<id>) ────────────────────────────
  componentOpen: (name: string) => `Se detaljer for ${name}`,
  componentNotFound:
    'Vi kunne ikke finde det system. Det er måske fjernet, eller linket hører til et andet område.',
  componentCurrentStatus: 'Aktuel status',

  // ── Day detail (/day/<YYYY-MM-DD>) ────────────────────────────────
  dayNotFound: 'Den dato findes ikke. Prøv at klikke på en dag i oversigten igen.',
  // Deliberately "vi har ikke registreret", not "der skete ikke noget": a day can
  // be coloured by an operator override with no published incident behind it, and
  // an incident that began before the 90-day window is not in this payload at all.
  dayEmpty: 'Vi har ikke registreret nogen hændelser på denne dag.',
  dayEmptyForComponent: (name: string) =>
    `Vi har ikke registreret nogen hændelser for ${name} på denne dag.`,
  dayLead: 'Hændelser, der var i gang denne dag:',
  dayLeadForComponent: (name: string) => `Hændelser for ${name}, der var i gang denne dag:`,
  dayOpen: (date: string) => `Se hændelser den ${date}`,
  componentPartOf: 'Del af',
  componentSubcomponents: 'Undersystemer',
  componentUptimeHeading: 'Oppetid',
  // The figure covers the same period as the bar beneath it, and the bar always
  // ends today — so this is "the last N days", not a fixed window.
  componentUptimePeriod: (days: number) => `Seneste ${days} dage`,
  componentDowntimeTotal: 'Nedetid i alt',
  componentDowntimeNone: 'Ingen registreret nedetid',
  componentIncidentsHeading: 'Hændelser for dette system',
  componentIncidentsEmpty: 'Der har ikke været registreret hændelser for dette system i perioden.',
  // A parent's figures roll its children up, so the number on a parent's page is
  // not "this box" but "everything under it" — say so rather than let a reader
  // wonder why a healthy-looking parent reports a child's outage.
  componentRollupNote: 'Tal og historik dækker dette system og alle undersystemer.',

  // ── Incidents ─────────────────────────────────────────────────────
  incidentsHeading: 'Hændelser',
  incidentsEmpty: 'Der har ikke været registreret hændelser i perioden.',
  incidentsNoMatch: 'Ingen hændelser matcher dine filtre.',
  incidentUpdates: 'Opdateringer',
  incidentUpdate: 'Opdatering',
  incidentAffectedComponents: 'Berørte systemer',
  incidentAffectedGroups: 'Berørte områder',
  incidentStarted: 'Startet',
  incidentResolved: 'Løst',
  incidentScheduled: 'Planlagt',
  incidentPinned: 'Fastgjort',
  incidentNotFound: 'Vi kunne ikke finde den hændelse. Den er måske fjernet, eller linket er forkert.',
  incidentDuration: (value: string) => `Varighed ${value}`,
  back: 'Tilbage til oversigten',

  // ── Filters ───────────────────────────────────────────────────────
  // The seven chips. Values come straight from the shared vocabulary so the
  // public chips and the admin facets can never drift apart.
  filterLabels: { ...STATUS_FILTER_LABEL_LABELS_DA },
  filterClear: 'Ryd filtre',
  filterHeading: 'Filtrér hændelser',
  searchPlaceholder: 'Søg i hændelser',
  searchLabel: 'Søg i hændelser',

  // ── Subscribe ─────────────────────────────────────────────────────
  subscribe: 'Tilmeld',
  subscribeTitle: 'Få besked om driftsstatus',
  subscribeIntro:
    'Vi sender dig besked, når der opstår en driftsforstyrrelse, og igen når den er løst. Du kan afmelde dig igen når som helst.',
  subscribeEmail: 'E-mailadresse',
  // Phone is OPTIONAL and says so in the label itself rather than in a note
  // underneath: a requirement a reader has to hunt for is one they will miss.
  subscribePhone: 'Mobilnummer (valgfrit)',
  // Sets the expectation up front: an SMS costs money and interrupts, so it is
  // reserved for the cases that warrant interrupting someone.
  subscribePhoneHint: 'Benyttes kun til akutte driftsforstyrrelser.',
  subscribeChannel: 'Hvordan vil du have besked?',
  subscribeChannelEmail: 'E-mail',
  subscribeChannelSms: 'SMS',
  subscribeSystems: 'Systemer',
  // The "none = all" rule is the default choice, so it is stated rather than
  // left to be discovered.
  subscribeSystemsHint: 'Vælg de systemer, du vil have besked om. Ingen valgt = alle systemer.',
  subscribeSystemsSearch: 'Søg i systemer',
  subscribeSystemsAll: 'Alle systemer',
  subscribeSystemsCount: (n: number) => `${n} valgt`,
  subscribeExpandSystem: (name: string) => `Vis undersystemer under ${name}`,
  subscribeCollapseSystem: (name: string) => `Skjul undersystemer under ${name}`,
  subscribeNoSystemMatch: 'Ingen systemer matcher din søgning.',
  subscribeAudiences: 'Kundegruppe',
  subscribeAudiencesHint: 'Vælg de områder, du vil have besked om. Ingen valgt = alle områder.',
  subscribeAudiencesAll: 'Alle områder',
  subscribeNeedDestination: 'Udfyld din e-mailadresse.',
  // Deliberately says nothing about channels being on or off — that is our
  // operational state, not the reader's business. It confirms we have them and
  // that a confirmation is coming, which is all they need.
  subscribeDeferred: 'Tak — vi har registreret din tilmelding. Du får en bekræftelse fra os.',
  subscribeSubmit: 'Tilmeld mig',
  subscribeSending: 'Sender …',
  subscribeSent: 'Tjek din indbakke — vi har sendt dig et link, du skal klikke på for at bekræfte tilmeldingen.',
  subscribeSentSms: 'Vi har sendt en kode til dit mobilnummer. Indtast koden herunder for at bekræfte tilmeldingen.',
  subscribeError: 'Tilmeldingen kunne ikke gennemføres. Prøv igen om lidt.',
  subscribeDisabled: 'Tilmelding er ikke aktiveret på denne side lige nu.',
  subscribeConfirmed: 'Din tilmelding er bekræftet. Tak!',
  subscribeInvalidEmail: 'Indtast en gyldig e-mailadresse.',
  subscribeInvalidPhone: 'Indtast et gyldigt mobilnummer.',
  subscribeSelectAudience: 'Vælg mindst ét område.',
  subscribeTooFast: 'Vent et øjeblik, og prøv så igen.',
  subscribeRateLimited: 'Der er sendt for mange anmodninger. Prøv igen om lidt.',
  unsubscribed: 'Du er nu afmeldt og får ikke flere beskeder.',

  // ── Subscription pages (/manage, /unsubscribe) ────────────────────
  manageTitle: 'Din tilmelding',
  manageConfirmed: 'Din tilmelding er bekræftet. Tak!',
  manageIntro: 'Her kan du ændre, hvad du får besked om — eller afmelde dig helt.',
  manageChannelEmail: 'Du får besked på e-mail.',
  manageChannelSms: 'Du får besked på SMS.',
  managePending: 'Din tilmelding mangler stadig at blive bekræftet. Tjek din indbakke for bekræftelseslinket.',
  manageAlreadyUnsubscribed: 'Du er afmeldt og får ikke flere beskeder. Du kan tilmelde dig igen nedenfor.',
  manageResubscribe: 'Tilmeld mig igen',
  // A bounced address is TERMINAL — the suppression list always wins, so it will
  // never receive anything again no matter what this page says. Telling someone
  // their subscription is active while nothing can reach them is the one thing
  // this page must not do.
  manageBounced:
    'Vi kunne ikke levere til denne adresse, så beskederne er sat på pause. Kontrollér adressen, og tilmeld dig igen fra driftsstatussiden — eller kontakt os, hvis du mener det er en fejl.',
  manageSave: 'Gem ændringer',
  manageSaved: 'Dine ændringer er gemt.',
  manageSaveFailed: 'Ændringerne kunne ikke gemmes. Prøv igen om lidt.',
  manageUnsubscribe: 'Afmeld alle beskeder',
  // The link is unguessable but it can be forwarded, and it does not expire —
  // so say plainly that anyone holding it can change these settings.
  manageLinkNote: 'Linket til denne side er personligt. Del det ikke — enhver med linket kan ændre din tilmelding.',
  manageNotFound:
    'Linket virkede ikke. Det er måske forældet, eller tilmeldingen er allerede fjernet. Du kan altid tilmelde dig igen fra driftsstatussiden.',
  unsubscribeTitle: 'Du er afmeldt',
  unsubscribeDone: 'Du får ikke flere driftsbeskeder fra C&B. Vi har ikke gemt nogen begrundelse, og du behøver ikke gøre mere.',
  unsubscribeWorking: 'Afmelder …',
  unsubscribeRegret: 'Var det en fejl?',
  unsubscribeUndo: 'Tilmeld mig igen',
  unsubscribeToOverview: 'Se driftsstatus',

  // ── SMS one-time code ─────────────────────────────────────────────
  otpPrompt: 'Indtast den 6-cifrede kode fra SMS’en',
  otpSubmit: 'Bekræft kode',
  otpInvalid: 'Koden er forkert. Prøv igen.',
  otpExpired: 'Koden er udløbet. Bed om en ny.',
  otpResend: 'Send en ny kode',
  otpLocked: 'Der er prøvet for mange gange. Bed om en ny kode.',

  // ── Bot protection ────────────────────────────────────────────────
  captchaUnavailable: 'Sikkerhedstjekket kunne ikke indlæses. Genindlæs siden, og prøv igen.',
  captchaPending: 'Fuldfør sikkerhedstjekket for at fortsætte.',
} as const;

export type StatusCopy = typeof da;
